/**
 * Résolveur mock du transport GraphQL — intercepte les opérations dans
 * graphql-client quand VITE_USE_MOCKS=true. Les hooks générés, mappers et
 * composants restent identiques à la prod : seul le réseau est simulé.
 *
 * Une opération sans handler passe au réseau réel (avec un warn console),
 * ce qui permet de mixer mock + API pendant la transition.
 */
import {
  createMockAnalysis,
  deactivateMockAnalysis,
  findMockAnalysis,
  mockAnalyses,
  mockAnalysisStats,
  mockNiches,
  mockProductsByStatus,
  mockQuotas,
  removeMockAnalysis,
  updateMockAnalysisName,
} from './mockDb';

export const MOCKS_ENABLED = import.meta.env.VITE_USE_MOCKS === 'true';

/** Latence simulée pour voir les skeletons/loaders comme en conditions réelles. */
export const MOCK_LATENCY_MS = 350;

const getOperationName = (query: string): string | null => {
  const match = /(?:query|mutation)\s+(\w+)/.exec(query);
  return match?.[1] ?? null;
};

type Vars = Record<string, any>;

const handlers: Record<string, (vars: Vars) => unknown> = {
  // ─── Analyses ────────────────────────────────────────────────────────────
  GetUserAnalysesPaginated: (vars) => ({
    getUserAnalysesPaginated: {
      analyses: mockAnalyses.filter((a) => a.type === (vars.type ?? 'catalog') && a.status !== 'removed'),
      nextCursor: null,
      hasNextPage: false,
    },
  }),

  GetAnalysisById: (vars) => ({
    getAnalysisById: findMockAnalysis(String(vars.id)) ?? null,
  }),

  GetPublicAnalysisCategories: () => ({
    getPublicAnalysisCategories: [
      {
        id: 'mock-public-cat-1',
        name: 'Sneakers',
        analyses: mockAnalyses.slice(0, 2).map((a) => ({ ...a, isPublic: true })),
      },
    ],
  }),

  // ─── Stats / niches ──────────────────────────────────────────────────────
  GetAnalysisStats: () => ({
    getAnalysisStats: mockAnalysisStats,
  }),

  GetAnalysisNiches: () => ({
    getAnalysisNiches: mockNiches,
  }),

  GetAnalysisBrandDistribution: () => ({
    getAnalysisBrandDistribution: mockNiches
      .filter((n) => n.type === 'brand')
      .map((n) => ({
        brand: n.niche,
        sellsCount: Math.round(n.productCount * (n.soldPct / 100)),
        totalSellsPrice: Math.round(n.productCount * (n.soldPct / 100) * n.avgPrice),
        avgSellsPrice: n.avgPrice,
      })),
  }),

  // ─── Produits ────────────────────────────────────────────────────────────
  GetProductsByStatus: (vars) => ({
    getProductsByStatus: mockProductsByStatus[String(vars.status)] ?? [],
  }),

  GetNicheSoldProducts: (vars) => ({
    getNicheSoldProducts: mockProductsByStatus.sold.filter(
      (p) => p.brand === vars.brandOrFullPath || String(vars.brandOrFullPath).includes(p.brand),
    ),
  }),

  GetAvailableItemConditions: (vars) => {
    const products = mockProductsByStatus[String(vars.status ?? 'sold')] ?? [];
    const counts = new Map<string, number>();
    for (const p of products) counts.set(p.itemCondition, (counts.get(p.itemCondition) ?? 0) + 1);
    return {
      getAvailableItemConditions: [...counts.entries()].map(([itemCondition, count]) => ({ itemCondition, count })),
    };
  },

  GetAvailableBrands: (vars) => {
    const products = mockProductsByStatus[String(vars.status ?? 'sold')] ?? [];
    const counts = new Map<string, number>();
    for (const p of products) counts.set(p.brand, (counts.get(p.brand) ?? 0) + 1);
    return {
      getAvailableBrands: [...counts.entries()].map(([brand, count]) => ({ brand, count })),
    };
  },

  // ─── User ────────────────────────────────────────────────────────────────
  GetCurrentUserAnalysisQuotas: () => ({
    getCurrentUserAnalysisQuotas: mockQuotas,
  }),

  GetCurrentUserVestiaireAccess: () => ({ getCurrentUserVestiaireAccess: true }),

  // ─── Mutations ───────────────────────────────────────────────────────────
  CreateAnalysis: (vars) => ({
    createAnalysis: createMockAnalysis({
      title: String(vars.input?.title ?? 'Recherche'),
      url: String(vars.input?.url ?? ''),
      type: vars.input?.type === 'profile' ? 'profile' : 'catalog',
    }),
  }),

  UpdateAnalysisName: (vars) => ({
    updateAnalysisName: updateMockAnalysisName(String(vars.id), String(vars.name)) ?? null,
  }),

  DeactivateAnalysis: (vars) => ({
    deactivateAnalysis: deactivateMockAnalysis(String(vars.id)) ?? null,
  }),

  RemoveDeactivatedAnalysis: (vars) => ({
    removeDeactivatedAnalysis: removeMockAnalysis(String(vars.id)) ?? null,
  }),

  ResetAnalysisNewSellsCounter: (vars) => {
    const analysis = findMockAnalysis(String(vars.id));
    if (analysis) analysis.newSells = 0;
    return { resetAnalysisNewSellsCounter: analysis ?? null };
  },
};

/**
 * Résout une opération mockée. Retourne `undefined` si aucun handler
 * n'existe (l'appelant doit alors passer au réseau réel).
 */
export const resolveMockOperation = (query: string, variables?: Vars): unknown => {
  const operationName = getOperationName(query);
  if (!operationName) return undefined;

  const handler = handlers[operationName];
  if (!handler) {
    console.warn(`[mocks] Pas de handler pour "${operationName}" — passage au réseau réel.`);
    return undefined;
  }
  return handler(variables ?? {});
};
